import StyledCard from "components/UI/StyledCard";
import EmptyStateSection from "../LandingPages/ui/LandingPageEditor/RulesPage/EmptyStateSection";
import DataGrid from "components/DataGrid/DataGrid";
import { gridClasses, GridColDef, GridColumnMenu, GridColumnMenuProps, GridEventListener } from "@mui/x-data-grid-premium";
import moment from "moment";
import { useTimezone } from "lib/TimezoneProvider";
import { useMemo, useState } from "react";
import IconButton from "@mui/material/IconButton";
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined';
import COLORS from 'lib/colors';
import { download, getFileExtension } from "lib/utils";
import React from "react";
import { UploadOrEditDocumentContainer } from "./ClientDocumentPage";


function DownloadButton({ clickHandler, label }) {
  return (
    <IconButton className='actionButton' onClick={(event)=>{ event.stopPropagation(); clickHandler(event) }} aria-label={label} size="small" sx={{color: COLORS.charcoal, padding: .5 }}>
      <DownloadIcon/>
    </IconButton>
  )
}

function CustomColumnMenu(props: GridColumnMenuProps) {
  return (
    <GridColumnMenu
      {...props}
      slots={{
        columnMenuSortItem: null,
        columnMenuColumnsItem: null,
        columnMenuPinningItem: null,
        columnMenuAggregationItem: null,
        columnMenuGroupingItem: null,
      }}
    />
  );
}

function DocumentTable({ data, refetch, userCanEdit }) {
  const [editDocument, setEditDocument] = useState({id: undefined})
  const { timezone } = useTimezone()

  const columns = useMemo((): GridColDef[] => {
    function Download({ rowNode }) {
      const document = data.find(d => d.id === rowNode.id)

      return (
        <DownloadButton
          clickHandler={(_e: unknown) => {
            // Fix me: this doesn't result in the downloaded file having the specified filename (instead it has the original filename)
            download(document.fileUrl as string, `${document.name}`)
          }}
          label='Download Document' />
      )
    }

    return [
      {
        headerName: "Last Updated",
        field: "updatedAt",
        type: "string",
        flex: 2,
        renderCell: (params) => {
          return moment.tz(params.value, timezone).format('M/D/YY, h:mm A');
        },
      } as const,
      {
        headerName: "File Type",
        field: "filename",
        type: "string",
        flex: 1.5,
        minWidth: 96,
        renderCell: (params) => {
          return getFileExtension(params.value)
        },
      } as const,
      {
        headerName: "Name",
        field: "name",
        type: "string",
        flex: 3,
      } as const,
      {
        headerName: "Description",
        field: "description",
        type: "string",
        flex: 6,
      } as const,
      {
        field: 'id',
        headerName: '',
        type: 'string',
        renderCell: Download,
        minWidth: 80,
        disableColumnMenu: true,
        sortable: false,
        filterable: false,
      } as const
    ]
  }, [data, timezone])

  const onRowClick = React.useCallback<GridEventListener<'rowClick'>>(
    ({ id }) => {
      if (userCanEdit){
        const document = data.find(d => d.id === id)
        setEditDocument({...document, fileUrl: null})
      }
    },
    [data, userCanEdit],
  )

  return (
    <>
      <DataGrid
        key={data?.length}
        onRowClick={onRowClick}
        columns={columns}
        rows={data || []}
        slots={{
          columnMenu: CustomColumnMenu,
        }}
        sx={{
          '.MuiDataGrid-footerContainer': {display: 'none'},
          "& .actionButton": {
            opacity: 0
          },
          [`& .${gridClasses.row}:hover`]: {
            ".actionButton": {
              opacity: 1
            }
          }
        }}
      />
      {!!editDocument?.id && (
        <UploadOrEditDocumentContainer onClose={() => { setEditDocument({id: undefined}) }} onComplete={() => { refetch(); setEditDocument({id: undefined}) }} isEditing={true} document={editDocument} />
      )}
    </>

  )
}

export default function UploadedDocuments({ userCanEdit = false, data, refetch }) {

  return (
    <>
      <StyledCard
        title='Uploaded Documents'
        isSubsection
        hasStickyHeader
        role="group"
      >
        {data.length === 0 &&
          <EmptyStateSection message={ userCanEdit ? 'No documents added.' : 'No documents available.'} />
        }
        {data.length > 0 &&
          <DocumentTable data={data} refetch={refetch} userCanEdit={userCanEdit} />
        }
      </StyledCard>
    </>
  )
}
